import { isEmpty, isNil } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import { configIdType, IId } from "@/core/api";
import { codelistType, ICodelist, skilllistType } from "@/data/codelists";
import { IFilter } from "@/data/filters";
import { configLocationType, ILocation, locationType } from "@/data/locations";
import { ISkillOption } from "@/views/JobRequests";

import { ISkill, skillListType } from "../skills";

export interface IJobRequestURLFilter {
  location?: ILocation;
  remote?: ICodelist[];
  query?: string;
  skillRoleCategories?: string[];
  id?: number;
  name?: string;
  radius?: number;
  signature?: string;
  dedicated?: boolean;
  originIds?: number[];
  frequency?: TEmailNotificationFrequency;
  favouritesOnly?: boolean;
  recommendedOnly?: boolean;
  languages?: ICodelist[];
  level?: ICodelist[];
  skillIds?: ISkillOption[];
  skills?: ISkill[];
}

export const configJobRequestURLFilterType: TType<IJobRequestURLFilter> = {
  location: v => (isEmpty(v?.locationId) ? undefined : locationType(v)),
  remote: v => (isNil(v) ? [] : ensureArray(codelistType)(v)),
  query: stringType(""),
  skillRoleCategories: v => (isNil(v) ? [] : ensureArray(stringType(undefined))(v)),
  id: numberType(undefined),
  name: stringType(undefined),
  frequency: stringType("DAILY"),
  radius: numberType(0),
  signature: stringType(undefined),
  dedicated: booleanType(false),
  originIds: ensureArray(numberType(null)),
  favouritesOnly: booleanType(false),
  recommendedOnly: booleanType(false),
  languages: v => (isNil(v) ? [] : ensureArray(codelistType)(v)),
  level: v => (isNil(v) ? [] : ensureArray(codelistType)(v)),
  skillIds: v => (isNil(v) ? [] : ensureArray(skilllistType)(v)),
  skills: v => (isNil(v) ? [] : skillListType(v)),
};

export const jobRequestURLFilterType = ensure(configJobRequestURLFilterType);

export interface IFiltersLocation extends ILocation {
  radius?: number;
}

const configFiltersLocationType = {
  ...configLocationType,
  radius: numberType(undefined),
};

export const filtersLocationType = ensure(configFiltersLocationType);

export interface IJobRequestFilterItems {
  location?: IFiltersLocation;
  remote?: string[];
  query?: string;
  filterType: string;
  skillRoleCategories: string[];
  dedicated?: boolean;
  originIds?: number[];
  favouritesOnly?: boolean;
  recommendedOnly?: boolean;
  languages?: string[];
  level?: string[];
  skillIds?: number[];
  skills?: ISkill[];
}

export const configJobRequestFilterItemsType: TType<IJobRequestFilterItems> = {
  location: v => (isNil(v) ? undefined : filtersLocationType(v)),
  remote: v => (isNil(v) ? [] : ensureArray(stringType(""))(v)),
  query: stringType(undefined),
  filterType: stringType("JOB_REQUEST"),
  skillRoleCategories: v => (isNil(v) ? [] : ensureArray(stringType(""))(v)),
  dedicated: booleanType(false),
  originIds: ensureArray(numberType(null)),
  favouritesOnly: booleanType(false),
  recommendedOnly: booleanType(false),
  languages: v => (isNil(v) ? [] : ensureArray(stringType(""))(v)),
  level: v => (isNil(v) ? [] : ensureArray(stringType(""))(v)),
  skillIds: v => (isNil(v) ? [] : ensureArray(numberType(-1))(v)),
  skills: v => (isNil(v) ? [] : skillListType(v)),
};

export const jobRequestFilterItemsType = ensure(configJobRequestFilterItemsType);

export type TEmailNotificationFrequency = "INSTANT" | "DAILY" | "NEVER";

export interface IJobRequestFilterDto extends IFilter<IJobRequestFilterItems> {
  frequency: TEmailNotificationFrequency;
}

export interface IJobRequestFilter extends IJobRequestFilterDto, IId {
  matchingElements?: number | null;
}

const configJobRequestFilterType: TType<IJobRequestFilter> = {
  ...configIdType,
  items: jobRequestFilterItemsType,
  name: stringType(""),
  frequency: stringType("DAILY"),
  matchingElements: numberType(null),
};

export const jobRequestFilterType = ensure(configJobRequestFilterType);
